import Pagination, { DEFAULT_PAGINATION_DATA } from './Pagination'

export default class TableControl<T, D = any> extends Pagination<T, D> {
    private _ascending: boolean = true
    private _sortBy?: string

    /**
     * The filter structure is entirely too complex to declare as generic.
     * Instead, the value is controlled outside this class by implementing code.
     * Reading values from the filter requires type assertion like `table.getFilter<T>(key)`.
     * While it is possible to use literally any value as a filter,
     *  it is recommended to use an array to wrap any number of options - even for single-option filters.
     */
    private _filters: Record<string, any> = {}

    static empty<T>() {
        return new TableControl<T>(DEFAULT_PAGINATION_DATA)
    }

    get sortBy(): string | undefined {
        return this._sortBy
    }

    get ascending() {
        return this._ascending
    }

    get ascendingInt(): number {
        if (!this._sortBy) return 0
        return this._ascending ? 1 : -1
    }

    restore(current: TableControl<T, D>) {
        this._sortBy = current.sortBy
        this._ascending = current.ascending
        this._filters = current.filters
    }

    sort(field: string, asc?: boolean) {
        // NOTE: Default asc logic toggles the value if no state was given.
        if (field === this._sortBy)
            this._ascending = typeof asc === 'boolean' ? asc : !this.ascending
        else {
            this._sortBy = field
            this._ascending = asc ?? true
        }
        this.fn()
    }

    get filters() {
        return this._filters
    }

    filter(field: string, value: any) {
        this._filters[field] = value
        this.fn()
    }

    resetFilter(field: string) {
        this.filter(field, undefined)
    }

    /**
     * Get the value of the given filter field.
     * The value can be anything but ideally it is an array of the desired type, even if only 1 option exists.
     * NOTE: This function requires implementing code to assert an explicite type.
     */
    getFilter<T = never>(field: string): T | undefined {
        return this._filters[field] as T
    }

    hasFilter(field: string) {
        return ![undefined, null].includes(this._filters[field])
    }

    /**
     * Determine if the filter for the given field contains the given value.
     * This function assumes the filter holds its options in an array.
     * Using an array even for single-option filters will enable
     *  the use of this function and make the filter easier to scale.
     */
    hasFilterState(field: string, value: any) {
        const state = this.getFilter<any[]>(field)
        if (!(state && Array.isArray(state))) return false
        return state.includes(value)
    }

    unpack<T extends Record<any, any | undefined>>(fieldSpec: T): T {
        const unpacked: any = {}
        for (const field of Object.keys(fieldSpec)) {
            const type = fieldSpec[field]
            unpacked[field] = this.getFilter<typeof type>(field)
        }
        return unpacked
    }
}
